'use es6';

import { fromJS, List, Map as ImmutableMap, Record } from 'immutable';
import ContentRoutes from 'ContentUtils/Routes';
import I18n from 'I18n';
import { stringify } from 'hub-http/helpers/params';
import { DRAFT_STATES, ERROR_STATES, NON_DEPLOYED_STATES, PageState, PUBLISHED_STATES, RequestStatus, SCHEDULED_STATES, THUNDERDOME_STATES, VARIANT_STATES } from '../constants/base';
import TranslatedContent from './TranslatedContent';
const DEFAULTS = {
  abTestId: 0,
  ampPreviewUrl: '',
  analyticsPageId: 0,
  approvalStatus: null,
  categoryId: null,
  compositionId: 0,
  contentGroupId: -1,
  contentStatus: 'default',
  data: ImmutableMap(),
  domain: null,
  dynamicPageDataSourceId: 0,
  dynamicPageDataSourceType: null,
  error: ImmutableMap(),
  id: 0,
  isPublished: false,
  isScheduled: false,
  isTestMaster: false,
  isVariant: false,
  language: '',
  mabExperimentId: 0,
  password: '',
  postBody: '',
  previewImageURL: '',
  processingStatus: '',
  propertyForDynamicPageSlug: '',
  publicAccessRules: List(),
  publicAccessRulesEnabled: false,
  resolvedDomain: null,
  screenshotPreviewTakenAt: 0,
  screenshotPreviewUrl: '',
  shareablePreviewURL: '',
  status: RequestStatus.UNINITIALIZED,
  templateType: null,
  updated: 0
};
const SCREENSHOT_CUTOFF_TIMESTAMP = 1451970000000;

const getAmpPreviewUrl = ({
  absoluteUrl,
  id,
  previewKey
}) => {
  if (!previewKey) {
    return absoluteUrl;
  }

  return ContentRoutes.getPreviewUrl(id, {
    cacheBust: null,
    hs_amp: true,
    preview_key: previewKey
  });
};

const getContentStatus = ({
  currentState,
  publishDate
}) => {
  if (currentState === PageState.PUBLISHED_OR_SCHEDULED) {
    if (publishDate && I18n.moment().isSameOrBefore(publishDate)) {
      return 'scheduled';
    }

    return 'published';
  } else if (PUBLISHED_STATES.includes(currentState)) {
    return 'published';
  } else if (DRAFT_STATES.includes(currentState)) {
    return 'draft';
  } else if (SCHEDULED_STATES.includes(currentState)) {
    return 'scheduled';
  } else if (ERROR_STATES.includes(currentState)) {
    return 'error';
  } else if (THUNDERDOME_STATES.includes(currentState)) {
    return 'thunderdome';
  }

  return 'default';
};

const getIsPublished = ({
  publishDate,
  state
}) => PUBLISHED_STATES.has(state) && I18n.moment().isAfter(publishDate);

const getIsScheduled = ({
  publishDate,
  state
}) => SCHEDULED_STATES.has(state) && I18n.moment().isSameOrBefore(publishDate);

const getIsVariant = ({
  mabVariant,
  state
}) => mabVariant || VARIANT_STATES.has(state);

const getIsTestMaster = ({
  abStatus,
  mabMaster
}) => abStatus === 'master' || mabMaster;

const getPreviewImageURL = data => {
  const {
    absoluteUrl,
    deletedAt,
    id,
    mabMaster,
    previewKey,
    isPublished
  } = data;

  if (deletedAt) {
    return '';
  }

  if (!previewKey || isPublished) {
    return mabMaster ? `${absoluteUrl}?hsNotRandom=true` : absoluteUrl;
  }

  return ContentRoutes.getPreviewUrl(id, {
    cacheBust: null,
    from_buffer: false,
    preview_key: previewKey
  });
};

const getScreenshotPreviewUrl = ({
  screenshotPreviewUrl,
  screenshotPreviewTakenAt,
  updated
}) => screenshotPreviewUrl && screenshotPreviewTakenAt >= SCREENSHOT_CUTOFF_TIMESTAMP && screenshotPreviewTakenAt >= updated ? screenshotPreviewUrl : '';

const getShareablePreviewURL = data => {
  const {
    absoluteUrl,
    currentState,
    deletedAt,
    id,
    previewKey
  } = data;

  if (deletedAt) {
    return '';
  }

  if (!previewKey || !(NON_DEPLOYED_STATES.includes(currentState) || getIsScheduled(data))) {
    return absoluteUrl;
  }

  const queryParams = stringify({
    hs_preview: `${previewKey}-${id}`
  });
  return `${absoluteUrl}?${queryParams}`;
};

const getUrlForExternalPage = ({
  canonicalUrl,
  canonicalizedurl,
  url
}) => canonicalUrl || canonicalizedurl || url && url[0];

const parseData = data => fromJS(data).update('translatedContent', translatedContent => translatedContent.map(tc => TranslatedContent.fromJS(tc.toObject())));

export default class Content extends Record(DEFAULTS) {
  static fromJS(data) {
    return new Content(Object.assign({}, data, {
      ampPreviewUrl: getAmpPreviewUrl(data),
      contentStatus: getContentStatus(data),
      data: parseData(data),
      isPublished: getIsPublished(data),
      isScheduled: getIsScheduled(data),
      isTestMaster: getIsTestMaster(data),
      isVariant: getIsVariant(data),
      previewImageURL: getPreviewImageURL(data),
      screenshotPreviewUrl: getScreenshotPreviewUrl(data),
      shareablePreviewURL: getShareablePreviewURL(data),
      status: RequestStatus.SUCCEEDED
    }));
  }

  static fromExternalPageJS(data) {
    const url = getUrlForExternalPage(data);
    return new Content({
      data: ImmutableMap({
        absoluteUrl: url,
        name: data.title,
        url
      }),
      previewImageURL: url,
      shareablePreviewURL: url,
      status: RequestStatus.SUCCEEDED
    });
  }

  isPublishedWithScheduledUpdate() {
    return this.isPublished && !!this.data.get('scheduledUpdateDate');
  }

  isComposing() {
    const compositionId = this.compositionId;
    const processingStatus = this.processingStatus;
    return compositionId > 0 && processingStatus !== null && processingStatus === 'COMPOSING';
  }

}